import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { MESSAGE_CHANNELS } from '@lensmira/domain'
import { formatDay, formatDisplayDate, formatDisplayTime } from '@lensmira/i18n'

import { toLoanFrameId } from '../api/write-results.js'
import { Ltr, useTranslation } from '../i18n.js'
import {
  MoneyInputError,
  formatWireForDisplay,
  parseMajorInput,
  parseMinorUnits,
} from '../money.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { sentence } from '../sentence.js'
import { chaseChannelWord, ownershipLabel, type Translate } from '../status-labels.js'
import { DataSurface } from './DataSurface.js'
import { frameName, type FrameNameParts } from './FrameChoice.js'
import type { StockLocationOption } from './Purchasing.js'
import type { RepairData } from './Repairs.js'
import type { StockPositionData } from './Stock.js'

/**
 * Loan frames. §6.27 `REP-005`; §15.13 steps 5 and 6 and its failure branch; §6.10 `INV-003`.
 *
 * `REP-005` `MUST`: "a stock item issued to a customer while their own is being repaired or
 * remade, with a deposit, an expected return date, a chase, and a write-off path." Those five
 * things are the whole screen: the four writes are issue, chase, return and write off.
 *
 * **It is built around the overdue list because that is the only list there is.** `GET
 * /loan-frames/overdue` is the one collection endpoint, and `LoanFramesService.overdue` filters its
 * rows through `isLoanFrameOverdue` before returning them, so a frame lent out this morning is not
 * in any list the counter can ask for. Two consequences are stated on screen rather than left for
 * somebody to discover. A loan that is still inside its date cannot be found at all, so a customer
 * who brings the frame back on time cannot be booked back in from here. And a loan that was just
 * issued would vanish the moment it was recorded, which is why the panel below opens the loan the
 * server just answered with: `useWriteSettled` carries the response, {@link toLoanFrameId} takes
 * the id off it, and the loan is then read back through `GET /loan-frames/:loanFrameId`. Both gaps
 * are an endpoint this vertical does not have, not a decision, and `later_todo.md` carries them.
 *
 * **Issuing needs a parent record, and only one kind of parent is reachable.** The body sends no
 * customer: `issue` reads the branch and the customer off the repair or the remake, "never from the
 * body, so a loan cannot be booked at a branch its repair is not at". `GET /repairs` gives the
 * repairs on the bench, so the repair path works. Nothing on the counter returns a remake id:
 * `reads.ts` has no remake read and `jobs.controller.ts` has only `POST remakes`, `POST
 * remakes/cost` and `POST remakes/new-job`. So this screen offers repairs only, and a loan against
 * a remake is unreachable until a remake read exists rather than a field left blank here.
 *
 * **The frame is picked off a shelf, for two refusals that would otherwise stop the queue.** The
 * server answers `LOAN_FRAME_NOT_A_FRAME` for a stock item with no `frame_variant_id` and
 * `LOAN_FRAME_OTHER_BRANCH` for one held somewhere other than the repair's branch. Both are
 * decidable here: `GET /stock/locations` and `GET /stock/positions` are branch-scoped by the same
 * `branchId` the repairs came from, and a position carries `frameVariantId`, so the picker offers
 * only frames and only this branch's. Nothing is typed by hand, because a typed id is the one way
 * to reach either refusal, and a refused write stops the whole queue until a person discards it.
 *
 * **`INV-003`'s `on_loan` is not what the writes do, and the screen says so.** §15.13 step 5 is
 * explicit: "It moves to `on loan`, not `sold`." The service is equally explicit that it does not:
 * "It does not move stock: no movement kind exists for a loan and `repair.ts` leaves that to the
 * stock module, so a frame out of the building still counts as on hand." So the frame stays `owned`
 * with its quantity unchanged, and the shelf screen will keep counting a frame that is on a
 * customer's face. That is a shop counting stock it cannot find, which is the §12 case 47 failure
 * arriving by a different door, so the issue panel states it instead of implying the ownership move
 * that `INV-003` registers an enum value for.
 *
 * **The deposit is a recorded amount and not money in the drawer.** The service says so in terms:
 * "`payment` is keyed to an order, so the cash a customer leaves is a recorded amount and not yet a
 * drawer or ledger entry." A panel that read like taking a payment would have a shopkeeper reaching
 * for the till and a cash count that never balances, so the words here are about recording a
 * figure.
 * `"0"` is a real answer, the same way `REP-004`'s free adjustment is: a shop that lends without a
 * deposit has made a decision, and a blank has not, so the field is required and zero is allowed.
 *
 * **The write-off is the one action on a different Appendix B row.** Every read and every other
 * write here takes `loan_frame_issue` ("Loan frame · issue": owner, manager, dispenser, optician).
 * The write-off takes `stock_adjust` (owner and manager, marked sensitive), because it forfeits a
 * customer's deposit. So it arrives as `canWriteOff` rather than being assumed from the fact that
 * the screen opened, for the reason `Purchasing` learned the hard way: a button offered to somebody
 * without the row is a 403 that stops the till.
 *
 * **A write-off with no recorded chase is refused, so it is not offered.** `validateLoanFrame`
 * throws `LOAN_FRAME_WRITE_OFF_WITHOUT_CHASE`, and its own comment gives the reasoning: §15.13's
 * failure branch is "chase ladder, then deposit forfeit and stock write-off", so the sequence is
 * enforced even though the ladder's length is a blank this product does not fill in. The panel
 * therefore says a chase has to be recorded first rather than offering a button that queues a
 * refusal behind every other write the shop makes that day.
 */

export interface LoanFrameChaseData {
  readonly attemptedAt: string
  readonly attemptedByStaffId: string
  /** Off the wire, so still a string per `ARC-020`. An unknown channel reads as itself. */
  readonly channel: string
  readonly note?: string
}

export interface LoanFrameWriteOffData {
  readonly writtenOffAt: string
  readonly writtenOffByStaffId: string
  readonly reason: string
  /**
   * What the shop kept, in minor units. The server splits the deposit, and nothing here
   * subtracts one figure from the other.
   */
  readonly retainedMinor: string
  readonly returnedMinor: string
}

export interface LoanFrameData {
  readonly id: string
  readonly branchId: string
  readonly customerId: string
  readonly repairId?: string
  readonly remakeId?: string
  readonly stockItemId: string
  readonly frameVariantId: string
  /**
   * The frame's name, as the frame picker spells it. Absent when the variant has left the
   * catalogue, which is the only case this screen has no name to print.
   */
  readonly frame?: FrameNameParts
  /** Minor units. `'0'` is a loan taken without a deposit, which is a decision and is printed. */
  readonly depositMinor: string
  readonly currency: string
  /** ISO instants, not branch-local dates, so they are formatted in the branch's own zone. */
  readonly issuedAt: string
  readonly expectedReturnAt: string
  readonly returnedAt?: string
  readonly returnedConditionNote?: string
  readonly writeOff?: LoanFrameWriteOffData
  readonly chases: readonly LoanFrameChaseData[]
  /** The server's own comparison against its clock, never this device's. */
  readonly overdue: boolean
  readonly version: number
}

export interface LoanFramesProps {
  /** `GET /loan-frames/overdue`. Already filtered to overdue by the server. */
  readonly load: () => Promise<readonly LoanFrameData[]>
  /** `GET /loan-frames/:loanFrameId`, which is how a loan the overdue list cannot hold is read. */
  readonly loadOne: (loanFrameId: string) => Promise<LoanFrameData>
  /**
   * `GET /repairs` with no stage, which is the server's open set: what is on the bench.
   *
   * `repair_intake_complete` is owner, manager, dispenser, cashier and optician, which contains
   * every role holding `loan_frame_issue`, so anybody who can open this screen can read this list.
   */
  readonly loadRepairs: () => Promise<readonly RepairData[]>
  readonly loadLocations: () => Promise<readonly StockLocationOption[]>
  /** `GET /stock/positions` for one shelf. `stock_view` is the row every role holds. */
  readonly loadFrames: (locationId: string) => Promise<readonly StockPositionData[]>
  /** The branch's currency, for the deposit field. Never guessed from a row. */
  readonly currency: string
  /** The branch's zone, so an issue date and a chase read as the shop's clock. */
  readonly timezone: string
  /**
   * The branch's own today, as `YYYY-MM-DD`.
   *
   * From the caller rather than `new Date()` here, for the reason `Promotions` gives: the server
   * compares against its own clock, and a device whose clock or zone is wrong would either refuse a
   * date that works or send one that `LOAN_FRAME_RETURN_BEFORE_ISSUE` refuses.
   */
  readonly today: string
  /** `stock_adjust`, the row the write-off takes. Owner and manager, and sensitive. */
  readonly canWriteOff: boolean
  readonly onOpenCustomer?: (customerId: string) => void
}

/** Returned or written off. Every write against one of these is refused, `LOAN_FRAME_CLOSED`. */
const isClosed = (loan: LoanFrameData): boolean =>
  loan.returnedAt !== undefined || loan.writeOff !== undefined

/**
 * A typed amount to a whole number of minor units for the wire, or the sentence saying why not.
 *
 * Module level and taking its words already translated, so it is a pure function rather than a
 * closure that would have to be a dependency of both `useCallback`s that use it. Both amounts on
 * this screen go through it, because they are refused for the same three reasons and a shop should
 * not meet two different wordings for a minus sign.
 *
 * **The negative check is the one that is not obvious.** `parseMajorInput` accepts a leading minus,
 * because an order line can carry a credit, and the server's `asMinor` is `^\d{1,18}$`. So a
 * deposit of `-500` parses cleanly here and comes back `AMOUNT_INVALID`, which blocks the queue.
 */
/**
 * The frame a loan went out as.
 *
 * Both lists printed `frameVariantId`, which is a UUID: a shopkeeper ringing a customer about an
 * overdue loan had nothing on screen naming the frame they were asking for. The name comes joined
 * on the read now, and this is the one place that decides what to show when it is missing.
 *
 * **Never the id as a fallback.** A variant that has left the catalogue is rare and a UUID helps
 * nobody at a counter, so the honest answer is to say the catalogue no longer has it. The loan's
 * deposit, dates and customer are all still on the row.
 */
function frameLabel(t: Translate, loan: { readonly frame?: FrameNameParts }): string {
  if (loan.frame === undefined) return t('loanFrames.frameNotInCatalogue')
  return frameName(loan.frame)
}

function minorUnits(
  typed: string,
  currency: string,
  words: { readonly blank: string; readonly bad: string; readonly negative: string },
): string | { readonly said: string } {
  if (typed.trim() === '') return { said: words.blank }
  let amount: bigint
  try {
    amount = parseMajorInput(typed, currency)
  } catch (parseError) {
    return { said: parseError instanceof MoneyInputError ? parseError.message : words.bad }
  }
  if (amount < 0n) return { said: words.negative }
  return amount.toString()
}

export function LoanFrames({
  load,
  loadOne,
  loadRepairs,
  loadLocations,
  loadFrames,
  currency,
  timezone,
  today,
  canWriteOff,
  onOpenCustomer,
}: LoanFramesProps): JSX.Element {
  const { t, locale } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [rows, setRows] = useState<readonly LoanFrameData[] | null>(null)
  const [opened, setOpened] = useState<LoanFrameData | null>(null)
  // Null is "not read yet". Both pickers below used to start empty and swallow a failed read into
  // empty, so a broken connection told the shopkeeper nothing was in for repair.
  const [repairs, setRepairs] = useState<readonly RepairData[] | null>(null)
  const [repairsError, setRepairsError] = useState<string | null>(null)
  const [repairsAttempt, setRepairsAttempt] = useState(0)
  const [locations, setLocations] = useState<readonly StockLocationOption[]>([])
  const [locationId, setLocationId] = useState('')
  const [frames, setFrames] = useState<readonly StockPositionData[] | null>(null)
  const [framesError, setFramesError] = useState<string | null>(null)
  const [framesAttempt, setFramesAttempt] = useState(0)
  const [repairId, setRepairId] = useState('')
  const [stockItemId, setStockItemId] = useState('')
  const [depositText, setDepositText] = useState('')
  const [returnBy, setReturnBy] = useState('')
  const [channel, setChannel] = useState('')
  const [chaseNote, setChaseNote] = useState('')
  const [conditionNote, setConditionNote] = useState('')
  const [writeOffReason, setWriteOffReason] = useState('')
  const [retainedText, setRetainedText] = useState('')
  const [error, setError] = useState<string | null>(null)
  /**
   * The overdue list failing to read, kept out of `error`. That one is shared with opening a loan
   * and with form validation, and a bad deposit typed into the form must not turn the list into an
   * error surface.
   */
  const [listError, setListError] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setRows(await load())
      setError(null)
      setListError(null)
    } catch (loadError) {
      setListError(loadError instanceof Error ? loadError.message : t('loanFrames.loadFailed'))
    }
  }, [load, t])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    let cancelled = false
    void loadRepairs()
      .then((loaded) => {
        if (cancelled) return
        setRepairs(loaded)
        setRepairsError(null)
      })
      .catch(() => {
        // Said at the picker and nowhere else: a repair list that failed to load is a reason the
        // issue form cannot be used, not a reason to cover the overdue list a shop is working down.
        if (!cancelled) setRepairsError(t('loanFrames.repairsFailed'))
      })
    return () => {
      cancelled = true
    }
  }, [loadRepairs, repairsAttempt, t])

  useEffect(() => {
    let cancelled = false
    void loadLocations()
      .then((loaded) => {
        if (cancelled) return
        setLocations(loaded)
        // The only shelf there is, is not a choice. More than one is: which shelf a loaner comes
        // off decides which stock item leaves the building.
        if (loaded.length === 1) setLocationId(loaded[0]?.id ?? '')
      })
      .catch(() => {
        if (!cancelled) setLocations([])
      })
    return () => {
      cancelled = true
    }
  }, [loadLocations])

  useEffect(() => {
    if (locationId === '') {
      setFrames(null)
      return
    }
    let cancelled = false
    setFrames(null)
    void loadFrames(locationId)
      .then((loaded) => {
        if (cancelled) return
        setFrames(
          loaded.filter(
            // Only a frame can be lent, which the server refuses with `LOAN_FRAME_NOT_A_FRAME`, and
            // a position with nothing on it has no frame to hand over. A negative quantity is §12
            // case 47 and belongs on the shelf screen that can act on it, not in a picker that
            // would offer a frame the shop does not have.
            (position) => position.frameVariantId !== undefined && position.quantity > 0,
          ),
        )
        setFramesError(null)
      })
      .catch(() => {
        if (!cancelled) setFramesError(t('loanFrames.framesFailed'))
      })
    return () => {
      cancelled = true
    }
  }, [loadFrames, locationId, framesAttempt, t])

  const open = useCallback(
    async (loanFrameId: string) => {
      try {
        // Read rather than taken from the row: the version a write sends has to be the one the
        // server last gave us, and a row from a list loaded minutes ago is not that.
        setOpened(await loadOne(loanFrameId))
        setError(null)
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : t('loanFrames.openFailed'))
      }
    },
    [loadOne, t],
  )

  useWriteSettled(snapshot, writeId, (write) => {
    setWriteId(null)
    setChannel('')
    setChaseNote('')
    setConditionNote('')
    setWriteOffReason('')
    setRetainedText('')
    // Only the issue form, and only on an issue. Every one of the four writes answers with the
    // loan, so a path check is what keeps a chase from wiping a half-typed loan behind it.
    if (write.path === '/loan-frames') {
      setRepairId('')
      setStockItemId('')
      setDepositText('')
      setReturnBy('')
    }
    // The loan the server just answered with, read back by its own id. This is the only way a loan
    // that is not yet overdue is visible at all: the overdue list cannot hold one, and an issue
    // that vanished on being recorded would have a shopkeeper issuing it twice.
    try {
      void open(toLoanFrameId(write.response))
    } catch (mismatch) {
      setError(mismatch instanceof Error ? mismatch.message : t('loanFrames.answerUnreadable'))
    }
    void refresh()
  })

  const issue = useCallback(async () => {
    if (repairId === '') {
      setError(t('loanFrames.repairRequired'))
      return
    }
    if (stockItemId === '') {
      setError(t('loanFrames.frameRequired'))
      return
    }
    const deposit = minorUnits(depositText, currency, {
      blank: t('loanFrames.depositRequired'),
      bad: t('loanFrames.amountBad'),
      negative: t('loanFrames.amountNegative'),
    })
    if (typeof deposit !== 'string') {
      setError(deposit.said)
      return
    }
    if (returnBy === '') {
      setError(t('loanFrames.returnByRequired'))
      return
    }
    if (returnBy <= today) {
      // `validateLoanFrame` refuses `expectedReturnAt < issuedAt`, and a bare `YYYY-MM-DD` is read
      // as midnight UTC, so today's date is always earlier than the moment the server records the
      // issue. Both strings are branch-local ISO dates, so the comparison is the date one.
      setError(t('loanFrames.returnByTooSoon'))
      return
    }
    setError(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/loan-frames',
      label: t('loanFrames.queueIssueLabel'),
      // No `expectedVersion`: this creates a row, so there is no version to be stale against. No
      // customer either, because `issue` takes it off the repair rather than trusting the body.
      body: {
        repairId,
        stockItemId,
        depositMinor: deposit,
        expectedReturnAt: returnBy,
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [currency, depositText, enqueue, repairId, returnBy, stockItemId, t, today])

  const chase = useCallback(
    async (loan: LoanFrameData) => {
      if (channel === '') {
        setError(t('loanFrames.channelRequired'))
        return
      }
      setError(null)
      const note = chaseNote.trim()
      const write = (await enqueue({
        method: 'POST',
        path: `/loan-frames/${loan.id}/chases`,
        label: t('loanFrames.queueChaseLabel'),
        // No `expectedVersion`, because a chase appends a row to `loan_frame_chase` rather than
        // editing the loan: `ChaseLoanFrame` does not declare one, the service does not read one,
        // and the loan's own version does not move. Sending one would be silently ignored, which is
        // worse than not sending it.
        body: { channel, ...(note === '' ? {} : { note }) },
      })) as { id: string }
      setWriteId(write.id)
    },
    [chaseNote, channel, enqueue, t],
  )

  const bookBack = useCallback(
    async (loan: LoanFrameData) => {
      setError(null)
      const note = conditionNote.trim()
      const write = (await enqueue({
        method: 'POST',
        path: `/loan-frames/${loan.id}/return`,
        label: t('loanFrames.queueReturnLabel'),
        body: {
          expectedVersion: loan.version,
          // §15.13 step 6 is "returned and re-inspected", and the note is what the inspection
          // produced. Optional on the wire, so a blank is left off rather than sent as an empty
          // string that would read as an inspection finding nothing wrong.
          ...(note === '' ? {} : { returnedConditionNote: note }),
        },
      })) as { id: string }
      setWriteId(write.id)
    },
    [conditionNote, enqueue, t],
  )

  const writeOff = useCallback(
    async (loan: LoanFrameData) => {
      if (loan.chases.length === 0) {
        // §15.13's failure branch is a ladder then a write-off, and `validateLoanFrame` enforces
        // the order with `LOAN_FRAME_WRITE_OFF_WITHOUT_CHASE`.
        setError(t('loanFrames.chaseFirst'))
        return
      }
      if (writeOffReason.trim() === '') {
        setError(t('loanFrames.reasonRequired'))
        return
      }
      const retained = minorUnits(retainedText, currency, {
        blank: t('loanFrames.retainedRequired'),
        bad: t('loanFrames.amountBad'),
        negative: t('loanFrames.amountNegative'),
      })
      if (typeof retained !== 'string') {
        setError(retained.said)
        return
      }
      if (parseMinorUnits(retained) > parseMinorUnits(loan.depositMinor)) {
        // `LOAN_FRAME_RETAINED_EXCEEDS_DEPOSIT`: "A frame worth more than its deposit is not
        // addressed by §15.13." Compared as `bigint`, because a deposit above
        // `Number.MAX_SAFE_INTEGER` compared as a `number` would pass a figure the server refuses.
        setError(t('loanFrames.retainedOverDeposit'))
        return
      }
      setError(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/loan-frames/${loan.id}/write-off`,
        label: t('loanFrames.queueWriteOffLabel'),
        body: {
          expectedVersion: loan.version,
          reason: writeOffReason.trim(),
          retainedDepositMinor: retained,
        },
      })) as { id: string }
      setWriteId(write.id)
    },
    [currency, enqueue, retainedText, t, writeOffReason],
  )

  const day = (iso: string): string => {
    const instant = Date.parse(iso)
    if (!Number.isFinite(instant)) return iso
    // The catalogue's own formatter, never `Intl` here: `D-03` and `TY-03` keep the counter on
    // Western digits in every locale.
    return formatDisplayDate(instant, timezone, locale)
  }

  const moment = (iso: string): string => {
    const instant = Date.parse(iso)
    if (!Number.isFinite(instant)) return iso
    return `${formatDisplayDate(instant, timezone, locale)} ${formatDisplayTime(instant, timezone, locale)}`
  }

  const line = statusLine(t, snapshot, writeId, t('queue.subject.loanFrame'))

  const detail = (loan: LoanFrameData): JSX.Element => {
    const closed = isClosed(loan)
    return (
      <div className="lm-loan__detail">
        <span className="lm-loan__frame">{frameLabel(t, loan)}</span>
        <span className="lm-loan__deposit" dir="ltr">
          {t('loanFrames.deposit', {
            amount: formatWireForDisplay(loan.depositMinor, loan.currency),
          })}
        </span>
        <span className="lm-loan__when">
          {sentence(t, 'loanFrames.issuedOn', { date: <Ltr>{day(loan.issuedAt)}</Ltr> })}
        </span>
        <span className="lm-loan__when">
          {sentence(t, 'loanFrames.dueOn', { date: <Ltr>{day(loan.expectedReturnAt)}</Ltr> })}
        </span>

        {onOpenCustomer === undefined ? null : (
          <button
            type="button"
            className="lm-button lm-button--secondary"
            onClick={() => {
              onOpenCustomer(loan.customerId)
            }}
          >
            {/* Neither the loan nor the repair carries a name, so the customer record is where a
                shopkeeper goes before ringing about a frame that has not come back. */}
            {t('loanFrames.openCustomer')}
          </button>
        )}

        {loan.chases.length === 0 ? (
          <p className="lm-hint">{t('loanFrames.noChases')}</p>
        ) : (
          <ul className="lm-chase-list">
            {loan.chases.map((attempt) => (
              <li key={`${attempt.attemptedAt}|${attempt.channel}`} className="lm-chase">
                <span className="lm-chase__channel">{chaseChannelWord(t, attempt.channel)}</span>
                <span className="lm-chase__when" dir="ltr">
                  <Ltr>{moment(attempt.attemptedAt)}</Ltr>
                </span>
                {attempt.note === undefined ? null : (
                  <span className="lm-chase__note">{attempt.note}</span>
                )}
              </li>
            ))}
          </ul>
        )}

        {loan.returnedAt === undefined ? null : (
          <p className="lm-loan__closed">
            {sentence(t, 'loanFrames.cameBack', { date: <Ltr>{day(loan.returnedAt)}</Ltr> })}
          </p>
        )}

        {loan.returnedConditionNote === undefined ? null : (
          <p className="lm-loan__condition">
            {t('loanFrames.condition', { note: loan.returnedConditionNote })}
          </p>
        )}

        {loan.writeOff === undefined ? null : (
          <>
            <p className="lm-loan__closed">
              {t('loanFrames.wasWrittenOff', { reason: loan.writeOff.reason })}
            </p>
            <p className="lm-loan__split" dir="ltr">
              {/* Both halves, because what the shop kept and what it owes back are different
                  sentences to the person holding the receipt. The server did the subtraction. */}
              {t('loanFrames.depositSplit', {
                kept: formatWireForDisplay(loan.writeOff.retainedMinor, loan.currency),
                back: formatWireForDisplay(loan.writeOff.returnedMinor, loan.currency),
              })}
            </p>
          </>
        )}

        {closed ? (
          // Every write against a closed loan is `LOAN_FRAME_CLOSED`, a 409 that blocks the queue.
          <p className="lm-hint">{t('loanFrames.alreadyClosed')}</p>
        ) : (
          <>
            <label className="lm-field lm-field--inline">
              <span className="lm-field__label">{t('loanFrames.channelLabel')}</span>
              <select
                className="lm-input"
                value={channel}
                onChange={(e) => {
                  setChannel(e.currentTarget.value)
                }}
              >
                <option value="">{t('loanFrames.channelUnset')}</option>
                {/* The domain's own five, not `V1_MESSAGE_CHANNELS`. A chase records what somebody
                    did to reach a customer rather than something this product sent, which is why
                    the service validates against `MESSAGE_CHANNELS` and `MSG-001`'s two v1 send
                    channels do not bound it. */}
                {MESSAGE_CHANNELS.map((option) => (
                  <option key={option} value={option}>
                    {chaseChannelWord(t, option)}
                  </option>
                ))}
              </select>
            </label>

            <label className="lm-field lm-field--inline">
              <span className="lm-field__label">{t('loanFrames.chaseNoteLabel')}</span>
              <input
                className="lm-input"
                autoComplete="off"
                value={chaseNote}
                onChange={(e) => {
                  setChaseNote(e.currentTarget.value)
                }}
              />
            </label>

            <button
              type="button"
              className="lm-button lm-button--secondary"
              onClick={() => void chase(loan)}
            >
              {t('loanFrames.recordChase')}
            </button>

            <label className="lm-field lm-field--inline">
              <span className="lm-field__label">{t('loanFrames.conditionLabel')}</span>
              <input
                className="lm-input"
                autoComplete="off"
                value={conditionNote}
                onChange={(e) => {
                  setConditionNote(e.currentTarget.value)
                }}
              />
            </label>

            <button
              type="button"
              className="lm-button lm-button--primary"
              onClick={() => void bookBack(loan)}
            >
              {t('loanFrames.bookBack')}
            </button>

            {canWriteOff ? (
              loan.chases.length === 0 ? (
                // Said rather than offered. §15.13's order is a ladder then a write-off, and the
                // button would queue a refusal that holds up every write behind it.
                <p className="lm-hint">{t('loanFrames.chaseFirst')}</p>
              ) : (
                <>
                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">{t('loanFrames.reasonLabel')}</span>
                    <input
                      className="lm-input"
                      autoComplete="off"
                      value={writeOffReason}
                      onChange={(e) => {
                        setWriteOffReason(e.currentTarget.value)
                      }}
                    />
                  </label>

                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">
                      {t('loanFrames.retainedLabel', { currency })}
                    </span>
                    <input
                      className="lm-input"
                      inputMode="decimal"
                      autoComplete="off"
                      value={retainedText}
                      onChange={(e) => {
                        setRetainedText(e.currentTarget.value)
                      }}
                    />
                  </label>
                  {/* No default, because no recovery policy exists to default from and the service
                      says so in its own refusal. Zero is a real answer and returns all of it. */}
                  <p className="lm-hint">{t('loanFrames.retainedMeans')}</p>

                  <button
                    type="button"
                    className="lm-button lm-button--danger"
                    onClick={() => void writeOff(loan)}
                  >
                    {t('loanFrames.writeOff')}
                  </button>
                </>
              )
            ) : null}
          </>
        )}
      </div>
    )
  }

  return (
    <section className="lm-screen" aria-labelledby="loan-frames-heading">
      <h1 id="loan-frames-heading" className="lm-screen__heading">
        {t('loanFrames.heading')}
      </h1>
      {/* The shape of the only list there is, said once at the top rather than inferred from an
          empty screen on a day when three frames are out and none of them is late yet. */}
      <p className="lm-hint">{t('loanFrames.onlyOverdue')}</p>

      {error === null ? null : (
        <p className="lm-hint" role="alert">
          {error}
        </p>
      )}

      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      {/*
        Empty here means nothing is late today, not that the shop has never lent a frame, so there is
        one sentence and no first-run invitation: lending is the form lower on this screen, and the
        hint above already says the list is only the overdue ones.
      */}
      <DataSurface
        state={
          listError !== null
            ? {
                kind: 'error',
                message: listError,
                onRetry: () => {
                  setListError(null)
                  void refresh()
                },
              }
            : rows === null
              ? { kind: 'loading' }
              : rows.length === 0
                ? { kind: 'empty' }
                : { kind: 'ready' }
        }
        shape="rows"
        rows={3}
        what={t('loanFrames.what')}
        emptyMessage={t('loanFrames.noneOverdue')}
      >
        <ul className="lm-loan-list">
          {(rows ?? []).map((row) => (
            <li key={row.id} className="lm-loan">
              <span className="lm-loan__frame">{frameLabel(t, row)}</span>
              <span className="lm-loan__due">
                {sentence(t, 'loanFrames.wasDue', { date: <Ltr>{day(row.expectedReturnAt)}</Ltr> })}
              </span>
              <span className="lm-loan__deposit" dir="ltr">
                {t('loanFrames.deposit', {
                  amount: formatWireForDisplay(row.depositMinor, row.currency),
                })}
              </span>
              <span className="lm-loan__chases">
                {t('loanFrames.chasesSoFar', { count: row.chases.length })}
              </span>

              <button
                type="button"
                className="lm-button lm-button--secondary"
                aria-expanded={opened?.id === row.id}
                onClick={() => {
                  if (opened?.id === row.id) {
                    setOpened(null)
                    return
                  }
                  void open(row.id)
                }}
              >
                {t('loanFrames.openIt')}
              </button>

              {opened?.id === row.id ? detail(opened) : null}
            </li>
          ))}
        </ul>
      </DataSurface>

      {/* A loan opened by id and not in the list above: the one the server just answered with, or
          one that has been closed since the list was loaded. */}
      {opened !== null && !rows?.some((row) => row.id === opened.id) ? (
        <div className="lm-loan lm-loan--standalone">
          <h2 className="lm-loan__heading">{t('loanFrames.thisLoan')}</h2>
          {detail(opened)}
        </div>
      ) : null}

      <h2 className="lm-loan-form__heading">{t('loanFrames.issueHeading')}</h2>
      {/* §15.13 step 5 says the stock moves to `on loan`; nothing in this vertical moves it. A
          shopkeeper reading the shelf list should know it is counting a frame that is not
          there. */}
      <p className="lm-hint">{t('loanFrames.stockUnmoved')}</p>

      <DataSurface
        state={
          repairsError !== null
            ? {
                kind: 'error',
                message: repairsError,
                onRetry: () => {
                  setRepairsError(null)
                  setRepairs(null)
                  setRepairsAttempt((n) => n + 1)
                },
              }
            : repairs === null
              ? { kind: 'loading' }
              : repairs.length === 0
                ? { kind: 'empty' }
                : { kind: 'ready' }
        }
        shape="lines"
        rows={1}
        what={t('loanFrames.repairsWhat')}
        emptyMessage={t('loanFrames.noRepairs')}
      >
        <label className="lm-field lm-field--inline">
          <span className="lm-field__label">{t('loanFrames.repairLabel')}</span>
          <select
            className="lm-input"
            value={repairId}
            onChange={(e) => {
              setRepairId(e.currentTarget.value)
            }}
          >
            <option value="">{t('loanFrames.repairUnset')}</option>
            {(repairs ?? []).map((repair) => (
              <option key={repair.id} value={repair.id}>
                {/* The condition and the promised date, because that is what a repair is known by
                    at the counter: neither the repair nor the loan carries a customer name. */}
                {t('loanFrames.repairOption', {
                  condition: repair.condition,
                  date: formatDay(repair.promisedDate, timezone, locale),
                })}
              </option>
            ))}
          </select>
        </label>
      </DataSurface>
      {/* A loan against a remake is `REP-005`'s other half and no counter read returns a remake id,
          so it is named rather than offered as a field somebody would have to paste a uuid
          into. */}
      <p className="lm-hint">{t('loanFrames.remakesElsewhere')}</p>

      {locations.length > 1 ? (
        <label className="lm-field lm-field--inline">
          <span className="lm-field__label">{t('loanFrames.shelfLabel')}</span>
          <select
            className="lm-input"
            value={locationId}
            onChange={(e) => {
              setLocationId(e.currentTarget.value)
              setStockItemId('')
            }}
          >
            <option value="">{t('loanFrames.shelfUnset')}</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {/* `BR-10` and `BR-04` travel with the name, as on every other shelf picker. */}
                {location.isCentralWarehouse
                  ? t('loanFrames.shelfWarehouse', { name: location.name })
                  : location.isSelling
                    ? location.name
                    : t('loanFrames.shelfNotSelling', { name: location.name })}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {/* Not drawn until a shelf is chosen: before that, "no frame on this shelf can be lent" is a
          claim about a shelf nobody has picked. */}
      {locationId === '' ? null : (
        <DataSurface
          state={
            framesError !== null
              ? {
                  kind: 'error',
                  message: framesError,
                  onRetry: () => {
                    setFramesError(null)
                    setFrames(null)
                    setFramesAttempt((n) => n + 1)
                  },
                }
              : frames === null
                ? { kind: 'loading' }
                : frames.length === 0
                  ? { kind: 'empty' }
                  : { kind: 'ready' }
          }
          shape="lines"
          rows={1}
          what={t('loanFrames.framesWhat')}
          emptyMessage={t('loanFrames.noFrames')}
        >
          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('loanFrames.frameLabel')}</span>
            <select
              className="lm-input"
              value={stockItemId}
              onChange={(e) => {
                setStockItemId(e.currentTarget.value)
              }}
            >
              <option value="">{t('loanFrames.frameUnset')}</option>
              {(frames ?? []).map((position) => (
                <option key={position.stockItemId} value={position.stockItemId}>
                  {/* The ownership word travels with the frame rather than filtering the list:
                    `INV-003` makes a consignment frame a vendor's property that the shop has not
                    bought, and lending one out of the building is a decision somebody should see
                    themselves making. The server allows it and the spec does not address it. */}
                  {t('loanFrames.frameOption', {
                    frame: position.frameVariantId ?? position.stockItemId,
                    lot: position.lot,
                    ownership: ownershipLabel(t, position.ownership),
                  })}
                </option>
              ))}
            </select>
          </label>
        </DataSurface>
      )}

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('loanFrames.depositLabel', { currency })}</span>
        <input
          className="lm-input"
          inputMode="decimal"
          autoComplete="off"
          value={depositText}
          onChange={(e) => {
            setDepositText(e.currentTarget.value)
          }}
        />
      </label>
      {/* Required, and zero allowed: lending without a deposit is a decision and a blank is not.
          And it is a recorded figure, not cash into the drawer, which the service says in
          terms. */}
      <p className="lm-hint">{t('loanFrames.depositMeans')}</p>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('loanFrames.returnByLabel')}</span>
        <input
          className="lm-input"
          type="date"
          value={returnBy}
          onChange={(e) => {
            setReturnBy(e.currentTarget.value)
          }}
        />
      </label>
      {/* The date is sent as typed and the server reads a bare date as midnight UTC, so the frame
          counts as late from the start of that day. No closing time is invented to soften it. */}
      <p className="lm-hint">{t('loanFrames.returnByMeans')}</p>

      <button type="button" className="lm-button lm-button--primary" onClick={() => void issue()}>
        {t('loanFrames.issue')}
      </button>
    </section>
  )
}
